import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { Text } from "@redotech/arbiter/text";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { sendMagicLink } from "@redotech/redo-partners-portal-rpc/schema/auth/send-magic-link";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { type FormEventHandler, memo, useState } from "react";

import { usePartnersPortalRpcClient } from "../rpc/client-context";
import { AuthLayout } from "./auth-layout";
import * as css from "./login.module.css";

export const LoginPage = memo(function LoginPage() {
  const rpcClient = usePartnersPortalRpcClient();
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [sentTo, setSentTo] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  // The server's schema, so the form fails before the request does.
  const validation = sendMagicLink.input.safeParse({ email });

  const handleSubmit: FormEventHandler<HTMLDivElement> = async (event) => {
    event.preventDefault();
    setEmailTouched(true);
    if (!validation.success) {
      return;
    }
    setError(undefined);
    setPending(true);
    try {
      await rpcClient.sendMagicLink(validation.data);
      setSentTo(validation.data.email);
    } catch (err) {
      // 4xx messages, such as the rate limit's, are safe to show.
      setError(
        clientErrorMessage(
          err,
          "Couldn't send the sign-in link. Please try again.",
        ),
      );
    } finally {
      setPending(false);
    }
  };

  if (sentTo) {
    return (
      <AuthLayout
        subtitle={
          <>
            We sent a sign-in link to{" "}
            <Text as="span" fontSize="sm" fontWeight="semibold" m="none">
              {sentTo}
            </Text>
            . It expires in 15 minutes.
          </>
        }
        title="Check your email"
      >
        <RedoButton
          hierarchy="link"
          IconLeading={ArrowLeftIcon}
          onClick={() => setSentTo(undefined)}
          size="sm"
          text="Use a different email"
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      subtitle="Enter your email and we'll send you a sign-in link."
      title="Sign in to Redo Partners"
    >
      <Flex as="form" dir="column" gap="3xl" onSubmit={handleSubmit}>
        <RedoTextInput
          autoFocus
          errorText={
            emailTouched && !validation.success
              ? "Enter a valid email"
              : undefined
          }
          fullWidth
          htmlId="login-email"
          inputTypeHint="email"
          label="Email address"
          onBlur={() => setEmailTouched(true)}
          placeholder="you@company.com"
          setValue={setEmail}
          value={email}
        />
        <Flex dir="column" gap="lg">
          <RedoButton
            className={css.fullWidthButton}
            disabled={!validation.success}
            hierarchy="primary"
            pending={pending}
            size="sm"
            text="Send sign-in link"
            type="submit"
          />
          {error && (
            <Text fontSize="xs" m="none" textColor="error">
              {error}
            </Text>
          )}
        </Flex>
      </Flex>
    </AuthLayout>
  );
});
